'use client';

import React from 'react';
import styled from 'styled-components';
import { LineChart } from '@/components/charts';

// ============================================================================
// Types
// ============================================================================

export interface MetricChartData {
  x: (number | string)[];
  y: number[];
}

export interface MetricChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  metricName: string;
  metricAcronym: string;
  metricUnit: string;
  chartData: MetricChartData;
  isLoading?: boolean;
  error?: string | null;
}

// ============================================================================
// Styled Components
// ============================================================================

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ModalTitleContainer = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  color: #6b7280;
  flex-shrink: 0;
  margin-left: 16px;
  
  &:hover {
    background: #f3f4f6;
    color: #ef4444;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  max-height: calc(90vh - 90px);
`;

const ChartWrapper = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  min-height: 400px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #6b7280;
  font-size: 14px;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #ef4444;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const EmptyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
`;

// ============================================================================
// Component
// ============================================================================

/**
 * MetricChartModal Component
 * 
 * Displays a modal with a line chart showing the trend of a specific Web Vital metric over time.
 * Opens when clicking the info icon on a WebVitalMetricCard.
 * 
 * @component
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * 
 * const chartData = {
 *   x: [1, 2, 3, 4, 5],
 *   y: [0.78, 0.82, 0.75, 0.80, 0.76]
 * };
 * 
 * <MetricChartModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   metricName="Network & Server Time"
 *   metricAcronym="TTFB"
 *   metricUnit="ms"
 *   chartData={chartData}
 * />
 * ```
 */
export const MetricChartModal: React.FC<MetricChartModalProps> = ({
  isOpen,
  onClose,
  metricName,
  metricAcronym,
  metricUnit,
  chartData,
  isLoading = false,
  error = null
}) => {
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleEscapeKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitleContainer>
            <ModalTitle>{metricAcronym} OVER TIME</ModalTitle>
            <ModalSubtitle>The 75th percentile of {metricName} ({metricAcronym}) over time.</ModalSubtitle>
          </ModalTitleContainer>
          <CloseButton onClick={onClose} aria-label="Close modal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {isLoading ? (
            <LoadingContainer>Loading chart data...</LoadingContainer>
          ) : error ? (
            <ErrorContainer>{error}</ErrorContainer>
          ) : chartData.x?.length === 0 ? (
            <EmptyContainer>No data available for this metric</EmptyContainer>
          ) : (
            <ChartWrapper>
              <LineChart
                title=""
                data={{
                  categories: chartData.x.map(String),
                  series: [{
                    name: metricAcronym,
                    data: chartData.y,
                    color: '#f59e0b',
                    strokeWidth: 2
                  }]
                }}
                showArea={false}
                height={400}
              />
            </ChartWrapper>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default MetricChartModal;

